import React from 'react';
import { connect } from 'react-redux';

import {
  workflowCreateFormSection,
  workflowRemoveFormSection,
  workflowSetFormDescription,
  workflowSetFormSectionName,
  workflowSortFormSections,
} from '~/actions/workflow';
import DescriptionTextAreaForm from '~/components/Workflow/shared/DescriptionTextAreaForm';
import StageItems from '~/components/Workflow/shared/StageItems';
import { useIntl } from 'react-intl';
import { getFormErrors } from '~/reducers/workflow';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Text from '~/ui/components/Text/Text';
import { coerceName } from '~/utils/workflow';

import Section from './Section';

function Form({
  // connected
  disabled,
  errors,
  form,
  workflowCreateFormSection,
  workflowRemoveFormSection,
  workflowSetFormDescription,
  workflowSetFormSectionName,
  workflowSortFormSections,
}) {
  const intl = useIntl();

  const sectionItems = form.sections.map((section, index) => ({
    ...section,
    title: coerceName(
      section.name,
      intl.formatMessage({
        id: 'Form.defaultSectionName',
        defaultMessage: 'Form Section',
        description: 'Default name for form sections',
      }),
      index,
    ),
    content: <Section position={index} sectionId={section.id} />,
  }));

  return (
    <>
      <FlexLayout flexDirection="column" space={4}>
        <FlexLayout disabled={disabled} flexDirection="column" space={2}>
          <Text variant="xs-dense-caps">
            {intl.formatMessage({
              id: 'Form.descriptionLabel',
              defaultMessage: 'Form Description',
              description: 'Label for form description section',
            })}
          </Text>
          <DescriptionTextAreaForm
            description={form.description}
            id="workflow--form-add-description"
            onUpdate={(description) =>
              workflowSetFormDescription({ description })
            }
          />
        </FlexLayout>
        {errors.questionCount === 0 && (
          <Text
            bg="red-100"
            color="red-600"
            p={4}
            sx={{ borderRadius: 'm' }}
            variant="xs-dense"
          >
            {intl.formatMessage({
              id: 'Form.questionCountError',
              defaultMessage:
                'At least one question should be created in a form.',
              description: 'Error message when form has no questions',
            })}
          </Text>
        )}
        {errors.circularDependencies && (
          <Text
            bg="red-100"
            color="red-600"
            p={4}
            sx={{ borderRadius: 'm' }}
            variant="xs-dense"
          >
            {intl.formatMessage({
              id: 'Form.circularDependencyError',
              defaultMessage:
                'Circular logic detected in at least one Field and/or Condition.',
              description: 'Error message for circular dependencies',
            })}
          </Text>
        )}
        {errors.lackCounterpartySignerQuestion && (
          <Text
            bg="red-100"
            color="red-600"
            p={4}
            sx={{ borderRadius: 'm' }}
            variant="xs-dense"
          >
            {intl.formatMessage({
              id: 'Form.counterpartySignerError',
              defaultMessage:
                'Questions for the counterparty name field and the counterparty email field are required for each counterparty signer.',
              description:
                'Error message for missing counterparty signer questions',
            })}
          </Text>
        )}
        <StageItems
          disabled={disabled}
          items={sectionItems}
          invalidPhaseIds={errors.invalidPhaseIds}
          removeModalTitle={intl.formatMessage({
            id: 'Form.removeModalTitle',
            defaultMessage: 'Remove Form Section',
            description: 'Title for remove section modal',
          })}
          sortModalTitle={intl.formatMessage({
            id: 'Form.sortModalTitle',
            defaultMessage: 'Reorder Form Sections',
            description: 'Title for sort sections modal',
          })}
          onRemove={workflowRemoveFormSection}
          onSort={workflowSortFormSections}
          onEditTitle={workflowSetFormSectionName}
          newItemAction={{
            id: 'workflow--form-add-section',
            text: intl.formatMessage({
              id: 'Form.addSectionButton',
              defaultMessage: 'Add a section',
              description: 'Button text to add a new section',
            }),
            handler: workflowCreateFormSection,
          }}
        />
      </FlexLayout>
    </>
  );
}

const mapStateToProps = ({ builder, workflow }) => ({
  disabled: !builder.shouldEnableAllComponents,
  errors: getFormErrors(workflow),
  form: workflow.form,
});

export default connect(mapStateToProps, {
  workflowCreateFormSection,
  workflowRemoveFormSection,
  workflowSetFormDescription,
  workflowSetFormSectionName,
  workflowSortFormSections,
})(Form);
